// frontend/app/CollectionViewerScreen.js
import React, { useEffect, useState } from "react";
import { useRoute, useNavigation } from "@react-navigation/native";
import { View, Text, FlatList, StyleSheet, ActivityIndicator, TouchableOpacity } from "react-native";
import { apiGetCollectionResolved } from "./collections";
import { normalizeQuizQuestions } from "./quizNormalize";

/**
 * Props:
 * - collectionId
 */
export default function CollectionViewerScreen() {
  const route = useRoute();
  const navigation = useNavigation();
  const collectionId = Number(route.params?.collectionId || 0);
  const [loading, setLoading] = useState(true);
  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        if (!collectionId) return;
        const data = await apiGetCollectionResolved(collectionId);
        if (mounted) setResolved(data);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [collectionId]);

  if (loading) return <View style={styles.center}><ActivityIndicator /></View>;
  if (!resolved) return <View style={styles.center}><Text style={{ color: "#888" }}>No data</Text></View>;

  const items = Array.isArray(resolved?.items) ? resolved.items : [];

  const renderItem = ({ item }) => {
    const quizQuestions = normalizeQuizQuestions(item?.quiz?.questions || []);
    return (
      <View style={styles.card}>
        {item.section ? <Text style={styles.sectionLabel}>{item.section}</Text> : null}
        <Text style={styles.title}>{item.title}</Text>
        <Text style={styles.subtle}>{item.subject} · {item.chapter} · {item.topic}</Text>
        <View style={{ height: 6 }} />
        {item.videos?.length ? <Text style={styles.groupHead}>🎞️ Video Segments ({item.videos.length})</Text> : null}
        {(item.videos || []).slice(0, 5).map(v => <Text key={`${v.id}-${v.tag_id}`} style={styles.row}>• {v.name}</Text>)}
        {item.pwos?.length ? <Text style={styles.groupHead}>🧪 PWO Scenes ({item.pwos.length})</Text> : null}
        {(item.pwos || []).slice(0, 5).map(p => <Text key={`${p.pwo_id}-${p.scene_name}`} style={styles.row}>• {p.name}</Text>)}
        {quizQuestions.length ? <Text style={styles.groupHead}>📝 Quiz ({quizQuestions.length})</Text> : null}
        {quizQuestions.slice(0, 3).map(q => (
          <Text key={q.question_id} style={styles.row}>
            • {(q?.question?.text || "").slice(0, 80)}{(q?.question?.text || "").length > 80 ? "…" : ""}
          </Text>
        ))}
      </View>
    );
  };

  return (
    <View style={{ flex: 1 }}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Text style={styles.backText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Preview</Text>
        <View style={{ width: 60 }} />
      </View>
      <FlatList
        data={items}
        keyExtractor={(_, idx) => String(idx)}
        renderItem={renderItem}
        contentContainerStyle={{ padding: 12, paddingBottom: 80 }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: "center", justifyContent: "center" },
  card: { backgroundColor: "#101214", borderRadius: 12, padding: 12, marginBottom: 10 },
  header: { height: 52, backgroundColor: "#0077b6", flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 12 },
  backBtn: { paddingVertical: 6, paddingHorizontal: 6 },
  backText: { color: "#fff", fontWeight: "700", fontSize: 16 },
  headerTitle: { color: "#fff", fontSize: 16, fontWeight: "700" },
  sectionLabel: { color: "#8ab4f8", fontWeight: "700", marginBottom: 4 },
  title: { color: "#fff", fontSize: 16, fontWeight: "700" },
  subtle: { color: "#99a1a8", marginTop: 2 },
  row: { color: "#d8dee9", marginVertical: 2 },
  groupHead: { color: "#eaeaea", fontWeight: "700", marginTop: 8 },
});
